import { useState, useEffect } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import {
  getTickets,
  getTicket,
  getTicketMessages,
  updateTicket,
  resolveTicket,
  reopenTicket,
  getTicketStats,
  syncTicketsFromPeriskope,
  toDisplayStatus,
  toPeriskopeStatus,
  toPeriskopeStatusFilter,
  type Ticket,
  type TicketStatus,
  type DisplayStatus,
  type TicketPriority,
  type TicketCategory,
  type TicketFilters,
  type TicketMessage,
} from "@/lib/api";
import { Layout } from "@/components/layout";
import { AIDraftSection } from "@/components/tickets/ai-draft-section";
import { ReportTechBugModal } from "@/components/tickets/report-tech-bug-modal";
import { Button } from "@/components/ui/button";
import { Sheet, SheetContent } from "@/components/ui/sheet";
import { Badge } from "@/components/ui/badge";
import { LabeledSelect } from "@/components/ui/labeled-select";
import { Switch } from "@/components/ui/switch";
import { Label } from "@/components/ui/label";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Loader2,
  MessageSquare,
  CheckCircle2,
  RotateCcw,
  ChevronLeft,
  ChevronRight,
  X,
  User,
  FileText,
  RefreshCw,
  Bug,
  ExternalLink,
  Copy,
  ClipboardList,
  Check,
} from "lucide-react";
import { formatDistanceToNow, format } from "date-fns";
import { cn } from "@/lib/utils";

const PAGE_SIZE = 25;

// Agents list
const AGENTS = [
  { email: "hello@atlas.kitchen", name: "Hello (Admin)" },
  { email: "aaron@atlas.kitchen", name: "Aaron" },
  { email: "therese@atlas.kitchen", name: "Therese" },
  { email: "shanneille@atlas.kitchen", name: "Shanneille" },
  { email: "kairaa@atlas.kitchen", name: "Kairaa" },
];

// Categories from Periskope
const CATEGORIES: { value: TicketCategory; label: string }[] = [
  { value: "needs_categorisation", label: "Needs categorisation" },
  { value: "billing", label: "Billing" },
  { value: "feature_request", label: "Feature request" },
  { value: "general_support", label: "General support" },
  { value: "hardware_issue", label: "Hardware issue" },
  { value: "logistics", label: "Logistics" },
  { value: "onboarding", label: "Onboarding" },
  { value: "payment_issue", label: "Payment issue" },
  { value: "tech_bug", label: "Tech bug" },
  { value: "tech_question", label: "Tech question" },
  { value: "tech_request", label: "Tech request" },
];

// Priority options
const PRIORITIES: { value: TicketPriority; label: string }[] = [
  { value: "needs_prioritisation", label: "Needs prioritisation" },
  { value: "standard", label: "Standard" },
  { value: "urgent", label: "Urgent" },
];

// Helper to check if a field needs attention (has default "needs" value)
function needsAttention(field: 'assignee' | 'category' | 'priority', value: string | null): boolean {
  if (field === 'assignee') return !value;
  if (field === 'category') return value === 'needs_categorisation';
  if (field === 'priority') return value === 'needs_prioritisation';
  return false;
}

// Map mutation updates to ticket fields
function mapUpdatesToTicket(updates: Parameters<typeof updateTicket>[1]): Partial<Ticket> {
  const mapped: Partial<Ticket> = {};
  if (updates.status !== undefined) mapped.status = updates.status;
  if (updates.priority !== undefined) mapped.priority = updates.priority;
  if (updates.assigneeEmail !== undefined) mapped.assignee_email = updates.assigneeEmail;
  if (updates.assigneeAdminUserId !== undefined) mapped.assignee_admin_user_id = updates.assigneeAdminUserId;
  if (updates.category !== undefined) mapped.category = updates.category;
  if (updates.isUrgent !== undefined) mapped.is_urgent = updates.isUrgent ? 1 : 0;
  if (updates.isOneResponseResolution !== undefined) mapped.is_one_response_resolution = updates.isOneResponseResolution ? 1 : 0;
  return mapped;
}

// Extract merchant/customer name for display
function extractMerchantName(ticket: Ticket): string {
  // First check merchant_name field (populated via webhook for new tickets)
  if (ticket.merchant_name) {
    // Remove common suffixes like "(Ops)", "(Admin)"
    return ticket.merchant_name
      .replace(/\s*\(Ops\)\s*$/i, '')
      .replace(/\s*\(Admin\)\s*$/i, '')
      .trim();
  }
  // Fallback to customer phone if available
  if (ticket.customer_phone) {
    return ticket.customer_phone;
  }
  return "—";
}

// Copy ID button — copies "ticket number | merchant"
function CopyIdButton({ ticketId, merchant }: { ticketId: string | null; merchant: string }) {
  const [copied, setCopied] = useState(false);
  const text = `#${ticketId || "—"} | ${merchant}`;
  return (
    <Button
      variant="ghost"
      size="sm"
      className="h-6 w-6 p-0 shrink-0"
      title="Copy ID"
      onClick={() => {
        navigator.clipboard.writeText(text);
        setCopied(true);
        setTimeout(() => setCopied(false), 1500);
      }}
    >
      {copied ? <Check className="h-3.5 w-3.5 text-emerald-600" /> : <Copy className="h-3.5 w-3.5 text-muted-foreground" />}
    </Button>
  );
}

// Copy details button — copies "ticket number | merchant | Title (link)"
function CopyDetailsButton({
  ticketId,
  merchant,
  subject,
  internalId,
}: {
  ticketId: string | null;
  merchant: string;
  subject: string | null;
  internalId: string;
}) {
  const [copied, setCopied] = useState(false);
  const url = `${window.location.origin}/tickets?ticket=${internalId}`;
  const title = subject || "No subject";
  const text = `#${ticketId || "—"} | ${merchant} | ${title} (${url})`;
  return (
    <Button
      variant="ghost"
      size="sm"
      className="h-6 w-6 p-0 shrink-0"
      title="Copy full details"
      onClick={() => {
        navigator.clipboard.writeText(text);
        setCopied(true);
        setTimeout(() => setCopied(false), 1500);
      }}
    >
      {copied ? <Check className="h-3.5 w-3.5 text-emerald-600" /> : <ClipboardList className="h-3.5 w-3.5 text-muted-foreground" />}
    </Button>
  );
}

// Status badge - displays simplified Open/Closed status
function StatusBadge({ status }: { status: TicketStatus }) {
  const displayStatus = toDisplayStatus(status);
  const styles: Record<DisplayStatus, string> = {
    open: "bg-blue-100 text-blue-800",
    closed: "bg-green-100 text-green-800",
  };
  const labels: Record<DisplayStatus, string> = {
    open: "Open",
    closed: "Closed",
  };
  return (
    <Badge variant="static" className={styles[displayStatus]}>
      {labels[displayStatus]}
    </Badge>
  );
}

// Priority badge
function PriorityBadge({ priority }: { priority: TicketPriority | null }) {
  if (!priority) return null;
  const styles: Record<TicketPriority, string> = {
    urgent: "bg-yellow-100 text-yellow-800",
    standard: "bg-gray-100 text-gray-700",
    needs_prioritisation: "bg-red-50 text-red-700 border border-red-300",
  };
  const labels: Record<TicketPriority, string> = {
    urgent: "Urgent",
    standard: "Standard",
    needs_prioritisation: "Needs prioritisation",
  };
  return (
    <Badge variant="static" className={styles[priority]}>
      {labels[priority]}
    </Badge>
  );
}

// Category badge
function CategoryBadge({ category }: { category: TicketCategory }) {
  const cat = CATEGORIES.find((c) => c.value === category);
  const needsFlag = category === 'needs_categorisation';
  return (
    <Badge
      variant="outline"
      className={cn(
        "text-xs",
        needsFlag && "border-red-300 bg-red-50 text-red-700"
      )}
    >
      {cat?.label || category}
    </Badge>
  );
}

// Image modal component
function ImageModal({ src, onClose }: { src: string; onClose: () => void }) {
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        onClose();
      }
    };
    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [onClose]);

  return (
    <div
      className="fixed inset-0 z-50 bg-black/80 flex items-center justify-center p-4"
      onClick={onClose}
    >
      <button
        className="absolute top-4 right-4 text-white hover:text-gray-300"
        onClick={onClose}
      >
        <X className="h-8 w-8" />
      </button>
      <img
        src={src}
        alt="Full size"
        className="max-w-full max-h-full object-contain"
        onClick={(e) => e.stopPropagation()}
      />
    </div>
  );
}

// Message bubble component
function MessageBubble({
  message,
  isTrigger,
  onImageClick,
}: {
  message: TicketMessage;
  isTrigger?: boolean;
  onImageClick?: (url: string) => void;
}) {
  const isInbound = message.direction === "inbound";
  const isNote = message.is_internal_note === 1;

  return (
    <div
      className={cn(
        "p-3 rounded-lg max-w-[85%]",
        isInbound
          ? "bg-muted mr-auto"
          : isNote
          ? "bg-yellow-50 border border-yellow-200 ml-auto"
          : "bg-primary/10 ml-auto",
        isTrigger && "ring-2 ring-blue-500 ring-offset-2"
      )}
    >
      <div className="flex items-center gap-2 mb-1">
        <span className="text-xs font-semibold">
          {isInbound
            ? message.sender_name || message.sender_phone || "Customer"
            : message.sender_name || "Agent"}
        </span>
        {isNote && (
          <Badge variant="outline" className="text-xs py-0">
            Note
          </Badge>
        )}
        {isTrigger && (
          <Badge className="text-xs py-0 bg-blue-100 text-blue-800">
            Ticket trigger
          </Badge>
        )}
        <span className="text-xs text-muted-foreground ml-auto">
          {format(new Date(message.periskope_timestamp || message.created_at), "MMM d, h:mm a")}
        </span>
      </div>
      {message.media_url && (
        <div className="mb-2">
          {message.message_type === "image" ? (
            <img
              src={message.media_url}
              alt="Attachment"
              className="max-w-full rounded border max-h-48 object-contain cursor-pointer hover:opacity-90 transition-opacity"
              onClick={() => onImageClick?.(message.media_url!)}
            />
          ) : (
            <a
              href={message.media_url}
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center gap-2 text-sm text-blue-600 hover:underline"
            >
              <FileText className="h-4 w-4" />
              View attachment
            </a>
          )}
        </div>
      )}
      <p className="text-sm whitespace-pre-wrap">{message.content}</p>
    </div>
  );
}

// Helper to get URL search param
function getUrlParam(key: string): string | null {
  const params = new URLSearchParams(window.location.search);
  return params.get(key);
}

// Helper to set URL search param
function setUrlParam(key: string, value: string | null) {
  const url = new URL(window.location.href);
  if (value) {
    url.searchParams.set(key, value);
  } else {
    url.searchParams.delete(key);
  }
  window.history.replaceState({}, "", url.toString());
}

export function TicketsPage() {
  const queryClient = useQueryClient();

  // Filter state - uses simplified display status (Open/Closed)
  const [statusFilter, setStatusFilter] = useState<DisplayStatus[]>(["open"]);
  const [assigneeFilter, setAssigneeFilter] = useState<string>("");
  const [categoryFilter, setCategoryFilter] = useState<string>("");
  const [openForHours, setOpenForHours] = useState<string>("");
  const [page, setPage] = useState(0);

  // Selection state - initialize from URL param if present
  const [selectedTicketId, setSelectedTicketId] = useState<string | null>(
    () => getUrlParam("id")
  );

  // Image modal state
  const [imageModalUrl, setImageModalUrl] = useState<string | null>(null);

  // Report tech bug modal state
  const [showBugModal, setShowBugModal] = useState(false);

  // Listen for clearTicketSelection event from nav clicks
  useEffect(() => {
    const handleClearSelection = () => {
      setSelectedTicketId(null);
    };
    window.addEventListener("clearTicketSelection", handleClearSelection);
    return () => window.removeEventListener("clearTicketSelection", handleClearSelection);
  }, []);

  // Sync URL param with selected ticket (for direct linking)
  useEffect(() => {
    const currentId = getUrlParam("id");
    if (selectedTicketId !== currentId) {
      setUrlParam("id", selectedTicketId);
    }
  }, [selectedTicketId]);

  // Build filters object - translate display statuses to Periskope statuses
  const filters: TicketFilters = {
    status: statusFilter.length > 0 ? toPeriskopeStatusFilter(statusFilter) : undefined,
    assignee: assigneeFilter && assigneeFilter !== "all" ? assigneeFilter : undefined,
    category: categoryFilter && categoryFilter !== "all" ? [categoryFilter as TicketCategory] : undefined,
    openForHours: openForHours && openForHours !== "all" ? parseInt(openForHours) : undefined,
  };

  // Fetch tickets - auto-refresh every 30s to show new webhook data
  const { data, isLoading } = useQuery({
    queryKey: ["tickets", filters, page],
    queryFn: () =>
      getTickets({
        limit: PAGE_SIZE,
        offset: page * PAGE_SIZE,
        sortBy: "created_at",
        sortOrder: "desc",
        filters,
      }),
    refetchInterval: 30000, // Refresh every 30 seconds
  });

  const totalPages = data?.total ? Math.ceil(data.total / PAGE_SIZE) : 0;

  // Fetch stats - auto-refresh every 30s, includes filters for filtered stats
  const { data: stats } = useQuery({
    queryKey: ["ticket-stats", filters],
    queryFn: () => getTicketStats(filters),
    staleTime: 30000,
    refetchInterval: 30000,
  });

  // Find selected ticket from list or fetch it
  const selectedTicketFromList = data?.tickets.find((t) => t.id === selectedTicketId);

  // Fetch selected ticket details if not in list
  const { data: ticketDetail } = useQuery({
    queryKey: ["ticket", selectedTicketId],
    queryFn: () => (selectedTicketId ? getTicket(selectedTicketId) : null),
    enabled: !!selectedTicketId && !selectedTicketFromList,
  });

  const selectedTicket = selectedTicketFromList || ticketDetail;

  // Fetch messages for selected ticket
  const { data: messagesData, isLoading: messagesLoading } = useQuery({
    queryKey: ["ticket-messages", selectedTicketId],
    queryFn: () => (selectedTicketId ? getTicketMessages(selectedTicketId) : null),
    enabled: !!selectedTicketId,
    refetchInterval: selectedTicketId ? 30000 : false, // Refresh messages every 30s when viewing a ticket
  });

  // Reset page when filters change
  useEffect(() => {
    setPage(0);
  }, [statusFilter, assigneeFilter, categoryFilter, openForHours]);

  // Mutations with optimistic updates for instant UI feedback
  const updateMutation = useMutation({
    mutationFn: ({ id, updates }: { id: string; updates: Parameters<typeof updateTicket>[1] }) =>
      updateTicket(id, updates),
    onMutate: async ({ id, updates }) => {
      // Cancel outgoing refetches
      await queryClient.cancelQueries({ queryKey: ["tickets"] });
      await queryClient.cancelQueries({ queryKey: ["ticket", id] });

      // Snapshot previous values
      const previousTickets = queryClient.getQueryData(["tickets", filters, page]);
      const previousTicket = queryClient.getQueryData(["ticket", id]);

      // Optimistically update the ticket in the list
      queryClient.setQueryData(
        ["tickets", filters, page],
        (old: { tickets: Ticket[]; total: number } | undefined) => {
          if (!old) return old;
          return {
            ...old,
            tickets: old.tickets.map((t) =>
              t.id === id ? { ...t, ...mapUpdatesToTicket(updates) } : t
            ),
          };
        }
      );

      // Optimistically update the selected ticket detail
      queryClient.setQueryData(["ticket", id], (old: Ticket | undefined) => {
        if (!old) return old;
        return { ...old, ...mapUpdatesToTicket(updates) };
      });

      return { previousTickets, previousTicket };
    },
    onError: (error, { id }, context) => {
      // Rollback on error
      if (context?.previousTickets) {
        queryClient.setQueryData(["tickets", filters, page], context.previousTickets);
      }
      if (context?.previousTicket) {
        queryClient.setQueryData(["ticket", id], context.previousTicket);
      }
      console.error("Failed to update ticket:", error);
      alert(`Failed to update ticket: ${error.message}`);
    },
    onSettled: () => {
      // Refetch to ensure server state
      queryClient.invalidateQueries({ queryKey: ["tickets"] });
      queryClient.invalidateQueries({ queryKey: ["ticket-stats"] });
    },
  });

  const resolveMutation = useMutation({
    mutationFn: (id: string) => resolveTicket(id),
    onMutate: async (id) => {
      await queryClient.cancelQueries({ queryKey: ["tickets"] });
      await queryClient.cancelQueries({ queryKey: ["ticket", id] });

      const previousTickets = queryClient.getQueryData(["tickets", filters, page]);
      const previousTicket = queryClient.getQueryData(["ticket", id]);

      // Optimistically mark as closed
      queryClient.setQueryData(
        ["tickets", filters, page],
        (old: { tickets: Ticket[]; total: number } | undefined) => {
          if (!old) return old;
          return {
            ...old,
            tickets: old.tickets.map((t) =>
              t.id === id ? { ...t, status: "closed" as const } : t
            ),
          };
        }
      );
      queryClient.setQueryData(["ticket", id], (old: Ticket | undefined) => {
        if (!old) return old;
        return { ...old, status: "closed" as const };
      });

      return { previousTickets, previousTicket };
    },
    onError: (error, id, context) => {
      if (context?.previousTickets) {
        queryClient.setQueryData(["tickets", filters, page], context.previousTickets);
      }
      if (context?.previousTicket) {
        queryClient.setQueryData(["ticket", id], context.previousTicket);
      }
      console.error("Failed to resolve ticket:", error);
      alert(`Failed to resolve ticket: ${error.message}`);
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: ["tickets"] });
      queryClient.invalidateQueries({ queryKey: ["ticket-stats"] });
    },
  });

  const reopenMutation = useMutation({
    mutationFn: (id: string) => reopenTicket(id),
    onMutate: async (id) => {
      await queryClient.cancelQueries({ queryKey: ["tickets"] });
      await queryClient.cancelQueries({ queryKey: ["ticket", id] });

      const previousTickets = queryClient.getQueryData(["tickets", filters, page]);
      const previousTicket = queryClient.getQueryData(["ticket", id]);

      // Optimistically mark as open
      queryClient.setQueryData(
        ["tickets", filters, page],
        (old: { tickets: Ticket[]; total: number } | undefined) => {
          if (!old) return old;
          return {
            ...old,
            tickets: old.tickets.map((t) =>
              t.id === id ? { ...t, status: "open" as const } : t
            ),
          };
        }
      );
      queryClient.setQueryData(["ticket", id], (old: Ticket | undefined) => {
        if (!old) return old;
        return { ...old, status: "open" as const };
      });

      return { previousTickets, previousTicket };
    },
    onError: (error, id, context) => {
      if (context?.previousTickets) {
        queryClient.setQueryData(["tickets", filters, page], context.previousTickets);
      }
      if (context?.previousTicket) {
        queryClient.setQueryData(["ticket", id], context.previousTicket);
      }
      console.error("Failed to reopen ticket:", error);
      alert(`Failed to reopen ticket: ${error.message}`);
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: ["tickets"] });
      queryClient.invalidateQueries({ queryKey: ["ticket-stats"] });
    },
  });

  const syncMutation = useMutation({
    mutationFn: syncTicketsFromPeriskope,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["tickets"] });
      queryClient.invalidateQueries({ queryKey: ["ticket-stats"] });
    },
    onError: (error) => {
      console.error("Failed to sync tickets:", error);
      alert(`Failed to sync tickets: ${error.message}`);
    },
  });

  const messages = messagesData?.messages || [];

  // Toggle status filter
  const toggleStatus = (status: DisplayStatus) => {
    setStatusFilter((prev) =>
      prev.includes(status) ? prev.filter((s) => s !== status) : [...prev, status]
    );
  };

  return (
    <Layout>
      <div className="flex flex-col h-[calc(100vh-3rem)]">
        {/* Header */}
        <div className="flex items-center justify-between mb-4">
          <h1 className="text-2xl font-bold">Tickets</h1>
          <Button
            variant="outline"
            size="sm"
            onClick={() => syncMutation.mutate()}
            disabled={syncMutation.isPending}
          >
            {syncMutation.isPending ? (
              <Loader2 className="h-4 w-4 animate-spin mr-2" />
            ) : (
              <RefreshCw className="h-4 w-4 mr-2" />
            )}
            Sync from Periskope
          </Button>
        </div>

        {/* Filters */}
        <div className="flex flex-wrap items-center gap-3 mb-4 pb-4 border-b">
          {/* Status chips - simplified to Open/Closed */}
          <div className="flex items-center gap-1">
            {(["open", "closed"] as DisplayStatus[]).map((status) => (
              <Button
                key={status}
                variant={statusFilter.includes(status) ? "default" : "outline"}
                size="sm"
                className="h-7 text-xs"
                onClick={() => toggleStatus(status)}
              >
                {status === "open" ? "Open" : "Closed"}
              </Button>
            ))}
          </div>

          <div className="h-6 w-px bg-border" />

          {/* Assignee */}
          <LabeledSelect
            value={assigneeFilter}
            onValueChange={setAssigneeFilter}
            options={[
              { value: "all", label: "All assignees" },
              { value: "unassigned", label: "Unassigned" },
              ...AGENTS.map((a) => ({ value: a.email, label: a.name })),
            ]}
            placeholder="Assignee"
            className="w-[160px] h-8 text-xs"
          />

          {/* Open For */}
          <LabeledSelect
            value={openForHours}
            onValueChange={setOpenForHours}
            options={[
              { value: "all", label: "Any time" },
              { value: "1", label: "< 1 hour" },
              { value: "24", label: "< 1 day" },
              { value: "48", label: "> 2 days" },
              { value: "168", label: "> 1 week" },
            ]}
            placeholder="Open for"
            className="w-[140px] h-8 text-xs"
          />

          {/* Category */}
          <LabeledSelect
            value={categoryFilter}
            onValueChange={setCategoryFilter}
            options={[
              { value: "all", label: "All categories" },
              ...CATEGORIES,
            ]}
            placeholder="Category"
            className="w-[160px] h-8 text-xs"
          />

          {/* Clear filters */}
          {(statusFilter.length !== 1 ||
            !statusFilter.includes("open") ||
            assigneeFilter ||
            categoryFilter ||
            openForHours) && (
            <Button
              variant="ghost"
              size="sm"
              className="h-7 text-xs"
              onClick={() => {
                setStatusFilter(["open"]);
                setAssigneeFilter("");
                setCategoryFilter("");
                setOpenForHours("");
              }}
            >
              <X className="h-3 w-3 mr-1" />
              Clear
            </Button>
          )}

          <div className="ml-auto text-xs text-muted-foreground">
            {data?.total || 0} tickets
          </div>
        </div>

        {/* Filtered Stats Bar - 3 mutually exclusive categories */}
        {stats && (
          <div className="grid grid-cols-3 gap-3 mb-4">
            <div className="bg-red-50 border border-red-200 rounded-lg p-3">
              <div className="text-2xl font-bold text-red-700">{stats.openUrgent || 0}</div>
              <p className="text-xs text-red-600">urgent tickets</p>
            </div>
            <div className="bg-orange-50 border border-orange-200 rounded-lg p-3">
              <div className="text-2xl font-bold text-orange-700">{stats.openOldNonUrgent || 0}</div>
              <p className="text-xs text-orange-600">tickets &gt; 2 days old</p>
            </div>
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
              <div className="text-2xl font-bold text-blue-700">{stats.openOther || 0}</div>
              <p className="text-xs text-blue-600">remaining open tickets</p>
            </div>
          </div>
        )}

        {/* Main content: Table + Detail Sheet */}
        <div className="flex gap-4 flex-1 min-h-0">
          {/* Table */}
          <div className="flex flex-col min-h-0 w-full">
            <div className="flex-1 overflow-auto border rounded-lg">
              <Table>
                <TableHeader className="sticky top-0 bg-background z-10">
                  <TableRow>
                    <TableHead className="w-[80px]">Ticket ID</TableHead>
                    <TableHead className="w-[90px]">Status</TableHead>
                    <TableHead className="w-[100px]">Assignee</TableHead>
                    <TableHead className="w-[120px]">Merchant</TableHead>
                    <TableHead>Subject</TableHead>
                    <TableHead className="w-[120px]">Category</TableHead>
                    <TableHead className="w-[120px]">Priority</TableHead>
                    <TableHead className="w-[90px]">Created</TableHead>
                    <TableHead className="w-[90px]">Last msg</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {isLoading ? (
                    <TableRow>
                      <TableCell colSpan={9} className="text-center py-8">
                        <Loader2 className="h-6 w-6 animate-spin mx-auto text-muted-foreground" />
                      </TableCell>
                    </TableRow>
                  ) : data?.tickets.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={9} className="text-center py-8 text-muted-foreground">
                        No tickets found
                      </TableCell>
                    </TableRow>
                  ) : (
                    data?.tickets.map((ticket) => (
                      <TableRow
                        key={ticket.id}
                        className={cn(
                          "cursor-pointer hover:bg-muted/50",
                          selectedTicketId === ticket.id && "bg-muted"
                        )}
                        onClick={() => setSelectedTicketId(ticket.id)}
                      >
                        <TableCell className="text-xs font-mono text-muted-foreground">
                          {ticket.periskope_ticket_id || "—"}
                        </TableCell>
                        <TableCell>
                          <StatusBadge status={ticket.status} />
                        </TableCell>
                        <TableCell>
                          <span className={cn(
                            "text-sm truncate block max-w-[100px]",
                            needsAttention('assignee', ticket.assignee_email) && "text-red-600 font-medium"
                          )}>
                            {ticket.assignee_email
                              ? AGENTS.find((a) => a.email === ticket.assignee_email)?.name || ticket.assignee_email.split("@")[0]
                              : "Needs assignment"}
                          </span>
                        </TableCell>
                        <TableCell>
                          <span className="text-sm truncate block max-w-[120px]">
                            {extractMerchantName(ticket)}
                          </span>
                        </TableCell>
                        <TableCell>
                          <p className="truncate text-sm">{ticket.subject || "—"}</p>
                        </TableCell>
                        <TableCell>
                          <CategoryBadge category={ticket.category} />
                        </TableCell>
                        <TableCell>
                          <PriorityBadge priority={ticket.priority} />
                        </TableCell>
                        <TableCell className="text-xs text-muted-foreground">
                          {formatDistanceToNow(new Date(ticket.created_at), { addSuffix: true })}
                        </TableCell>
                        <TableCell className="text-xs text-muted-foreground">
                          {ticket.last_message_at
                            ? formatDistanceToNow(new Date(ticket.last_message_at), { addSuffix: true })
                            : "—"}
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </div>

            {/* Pagination */}
            {totalPages > 1 && (
              <div className="flex items-center justify-between py-2">
                <span className="text-xs text-muted-foreground">
                  Page {page + 1} of {totalPages}
                </span>
                <div className="flex gap-1">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setPage((p) => Math.max(0, p - 1))}
                    disabled={page === 0}
                  >
                    <ChevronLeft className="h-4 w-4" />
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setPage((p) => Math.min(totalPages - 1, p + 1))}
                    disabled={page >= totalPages - 1}
                  >
                    <ChevronRight className="h-4 w-4" />
                  </Button>
                </div>
              </div>
            )}
          </div>

          {/* Detail Sheet */}
          <Sheet open={!!selectedTicket} onOpenChange={(open) => !open && setSelectedTicketId(null)}>
            <SheetContent side="right" className="border-l">
              {selectedTicket && (
              <>
              {/* Unified Header */}
              <div className="p-4 border-b flex-shrink-0">
                {/* Top row: Ticket ID | Merchant name + close button */}
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <span className="text-lg font-mono text-muted-foreground">
                      #{selectedTicket.periskope_ticket_id || "—"}
                    </span>
                    <span className="text-lg text-muted-foreground">|</span>
                    <span className="text-lg font-medium">
                      {extractMerchantName(selectedTicket)}
                    </span>
                    <CopyIdButton
                      ticketId={selectedTicket.periskope_ticket_id}
                      merchant={extractMerchantName(selectedTicket)}
                    />
                    <CopyDetailsButton
                      ticketId={selectedTicket.periskope_ticket_id}
                      merchant={extractMerchantName(selectedTicket)}
                      subject={selectedTicket.subject}
                      internalId={selectedTicket.id}
                    />
                  </div>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => setSelectedTicketId(null)}
                  >
                    <X className="h-4 w-4" />
                  </Button>
                </div>

                {/* Opened time */}
                <p className="text-xs text-muted-foreground mt-1">
                  Opened {formatDistanceToNow(new Date(selectedTicket.created_at), { addSuffix: true })}
                </p>

                {/* Title - prominent */}
                <h2 className="font-semibold text-xl mt-3 mb-3">
                  {selectedTicket.subject || "No subject"}
                </h2>

                {/* Attribute controls row */}
                <div className="flex flex-wrap items-center gap-2">
                  {/* Status - simplified to Open/Closed */}
                  <LabeledSelect
                    value={toDisplayStatus(selectedTicket.status)}
                    onValueChange={(value) =>
                      updateMutation.mutate({
                        id: selectedTicket.id,
                        updates: { status: toPeriskopeStatus(value as DisplayStatus) },
                      })
                    }
                    options={[
                      { value: "open", label: "Open" },
                      { value: "closed", label: "Closed" },
                    ]}
                    className="w-[100px] h-8"
                  />

                  {/* Priority */}
                  <LabeledSelect
                    value={selectedTicket.priority || "needs_prioritisation"}
                    onValueChange={(value) =>
                      updateMutation.mutate({
                        id: selectedTicket.id,
                        updates: { priority: value as TicketPriority },
                      })
                    }
                    options={PRIORITIES}
                    className={cn(
                      "w-[140px] h-8",
                      needsAttention('priority', selectedTicket.priority) && "border-red-500 bg-red-50"
                    )}
                  />

                  {/* Assignee */}
                  <LabeledSelect
                    value={selectedTicket.assignee_email || "unassigned"}
                    onValueChange={(value) =>
                      updateMutation.mutate({
                        id: selectedTicket.id,
                        updates: { assigneeEmail: value === "unassigned" ? null : value },
                      })
                    }
                    options={[
                      { value: "unassigned", label: "Needs assignment" },
                      ...AGENTS.map((a) => ({ value: a.email, label: a.name })),
                    ]}
                    icon={<User className="h-3 w-3 mr-1" />}
                    className={cn(
                      "w-[140px] h-8",
                      needsAttention('assignee', selectedTicket.assignee_email) && "border-red-500 bg-red-50"
                    )}
                  />

                  {/* Category */}
                  <LabeledSelect
                    value={selectedTicket.category}
                    onValueChange={(value) =>
                      updateMutation.mutate({
                        id: selectedTicket.id,
                        updates: { category: value as TicketCategory },
                      })
                    }
                    options={CATEGORIES}
                    className={cn(
                      "w-[150px] h-8",
                      needsAttention('category', selectedTicket.category) && "border-red-500 bg-red-50"
                    )}
                  />

                  {/* Report Tech Bug - only show if not already reported */}
                  {!selectedTicket.slack_bug_thread_url && (
                    <Button
                      variant="outline"
                      size="sm"
                      className="h-8"
                      onClick={() => setShowBugModal(true)}
                    >
                      <Bug className="h-4 w-4 mr-1" />
                      Report bug
                    </Button>
                  )}

                  {/* Right side: 1-response toggle + Resolve/Reopen */}
                  <div className="ml-auto flex items-center gap-3">
                    <div className="flex items-center gap-2">
                      <Switch
                        id="one-response"
                        checked={selectedTicket.is_one_response_resolution === 1}
                        onCheckedChange={(checked) =>
                          updateMutation.mutate({
                            id: selectedTicket.id,
                            updates: { isOneResponseResolution: checked },
                          })
                        }
                      />
                      <Label htmlFor="one-response" className="text-xs text-muted-foreground cursor-pointer whitespace-nowrap">
                        1-response resolution
                      </Label>
                    </div>

                    {toDisplayStatus(selectedTicket.status) === "open" ? (
                      <Button
                        size="sm"
                        className="h-8"
                        onClick={() => resolveMutation.mutate(selectedTicket.id)}
                        disabled={resolveMutation.isPending}
                      >
                        {resolveMutation.isPending ? (
                          <Loader2 className="h-4 w-4 animate-spin mr-1" />
                        ) : (
                          <CheckCircle2 className="h-4 w-4 mr-1" />
                        )}
                        Resolve
                      </Button>
                    ) : (
                      <Button
                        variant="outline"
                        size="sm"
                        className="h-8"
                        onClick={() => reopenMutation.mutate(selectedTicket.id)}
                        disabled={reopenMutation.isPending}
                      >
                        {reopenMutation.isPending ? (
                          <Loader2 className="h-4 w-4 animate-spin mr-1" />
                        ) : (
                          <RotateCcw className="h-4 w-4 mr-1" />
                        )}
                        Reopen
                      </Button>
                    )}
                  </div>
                </div>
              </div>

              {/* Tech bug reported bar */}
              {selectedTicket.slack_bug_thread_url && (
                <div className="px-4 py-2 border-b bg-green-50 flex items-center justify-between flex-shrink-0">
                  <span className="text-sm text-green-700">Tech bug reported in Slack.</span>
                  <Button
                    variant="outline"
                    size="sm"
                    className="h-7 text-xs"
                    asChild
                  >
                    <a
                      href={selectedTicket.slack_bug_thread_url}
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      <ExternalLink className="h-3 w-3 mr-1" />
                      View in Slack
                    </a>
                  </Button>
                </div>
              )}

              {/* Main Content: Messages (left 60%) + AI Draft (right 40%) */}
              <div className="flex-1 flex gap-4 p-4 min-h-0">
                {/* Messages Column - Periskope Chat */}
                <div className="w-3/5 flex flex-col min-h-0 border rounded-lg">
                  <div className="px-3 py-2 border-b bg-muted/30 flex-shrink-0">
                    <span className="text-xs font-medium text-muted-foreground">Periskope Chat</span>
                  </div>
                  <div className="flex-1 overflow-auto p-4 space-y-3">
                    {messagesLoading ? (
                      <div className="flex items-center justify-center py-8">
                        <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
                      </div>
                    ) : messages.length === 0 ? (
                      <div className="flex flex-col items-center justify-center py-8 text-muted-foreground">
                        <MessageSquare className="h-8 w-8 mb-2 opacity-30" />
                        <p>No messages</p>
                      </div>
                    ) : (
                      messages.map((msg) => (
                        <MessageBubble
                          key={msg.id}
                          message={msg}
                          isTrigger={!!selectedTicket.trigger_message_id && msg.periskope_message_id === selectedTicket.trigger_message_id}
                          onImageClick={setImageModalUrl}
                        />
                      ))
                    )}
                  </div>
                </div>

                {/* AI Draft Column */}
                <div className="w-2/5 flex flex-col min-h-0">
                  {selectedTicket.has_ai_draft === 1 ? (
                    <AIDraftSection
                      ticketId={selectedTicket.id}
                      ticketStatus={selectedTicket.status}
                      currentCategory={selectedTicket.category}
                      onCategoryChange={(category) =>
                        updateMutation.mutate({
                          id: selectedTicket.id,
                          updates: { category },
                        })
                      }
                    />
                  ) : (
                    <div className="flex flex-col items-center justify-center h-full text-muted-foreground border rounded-lg">
                      <MessageSquare className="h-8 w-8 mb-2 opacity-30" />
                      <p className="text-sm">No AI draft available</p>
                    </div>
                  )}
                </div>
              </div>

              </>
              )}
            </SheetContent>
          </Sheet>
        </div>
      </div>

      {/* Image modal */}
      {imageModalUrl && (
        <ImageModal src={imageModalUrl} onClose={() => setImageModalUrl(null)} />
      )}

      {/* Report Tech Bug Modal */}
      {selectedTicket && (
        <ReportTechBugModal
          open={showBugModal}
          onOpenChange={setShowBugModal}
          ticket={selectedTicket}
          onSuccess={(threadUrl) => {
            // Update local state to show the "Tech bug reported" bar
            queryClient.setQueryData(["ticket", selectedTicket.id], (old: Ticket | undefined) => {
              if (!old) return old;
              return { ...old, slack_bug_thread_url: threadUrl };
            });
          }}
        />
      )}
    </Layout>
  );
}
